import { Component } from '@theme/component';

const MOBILE_VOLUME_PRICING_MEDIA_QUERY = '(max-width: 750px) or (pointer: coarse)';
let mobileVolumePricingListenersBound = false;
let lastScrollY = window.scrollY;

function closeAllVolumePricingPopovers() {
  document.querySelectorAll('.volume-pricing-info__popover').forEach((popover) => {
    /** @type {HTMLElement & { showPopover?: () => void; hidePopover?: () => void }} */
    const popoverElement = /** @type {any} */ (popover);
    if (popoverElement.matches(':popover-open')) {
      popoverElement.hidePopover();
    }
    popoverElement.removeAttribute('data-mobile-position');
    popoverElement.removeAttribute('data-mobile-placement');
    popoverElement.style.removeProperty('--volume-pricing-mobile-left');
    popoverElement.style.removeProperty('--volume-pricing-mobile-top');
  });
}

function bindMobileVolumePricingListeners() {
  if (mobileVolumePricingListenersBound) return;

  const handleMobileScroll = () => {
    if (!window.matchMedia(MOBILE_VOLUME_PRICING_MEDIA_QUERY).matches) return;
    
    // Check if the scroll is vertical and significant enough to warrant closing
    const currentScrollY = window.scrollY;
    const verticalScrollDelta = Math.abs(currentScrollY - lastScrollY);
    
    if (verticalScrollDelta > 10) { // Significant vertical scroll
      if (document.querySelector('.volume-pricing-info__popover:popover-open')) {
        closeAllVolumePricingPopovers();
      }
    }
    
    lastScrollY = currentScrollY;
  };

  window.addEventListener('scroll', handleMobileScroll, { passive: true, capture: true });
  mobileVolumePricingListenersBound = true;
}

/**
 * Displays volume pricing information in a popover.
 * Shows quantity rules and pricing tiers with the current tier highlighted.
 * Positioning and hover behavior is handled by the parent anchored-popover-component.
 *
 * @typedef {Object} VolumePricingInfoRefs
 * @property {HTMLElement} popover - The popover element
 *
 * @extends {Component<VolumePricingInfoRefs>}
 */
class VolumePricingInfoComponent extends Component {
  /**
   * Ensures the parent anchored-popover-component refs are refreshed after connection,
   */
  connectedCallback() {
  super.connectedCallback();
  const anchoredPopover = this.closest('anchored-popover-component');
  if (anchoredPopover instanceof Component && anchoredPopover.isConnected) {
    try {
      anchoredPopover.updatedCallback();
    } catch (error) {
      Promise.resolve().then(() => {
        if (anchoredPopover.isConnected) {
          try {
            anchoredPopover.updatedCallback();
          } catch (e) {}
        }
      });
    }
  }

  this.#setupTriggerClick();
  bindMobileVolumePricingListeners();
}

#setupTriggerClick() {
  const anchoredPopover = this.closest('anchored-popover-component');
  const trigger = anchoredPopover instanceof Component ? anchoredPopover.refs?.trigger : null;
  if (!trigger) return;

  /** @param {Event} event */
  const stopGesturePropagation = (event) => {
    if (!window.matchMedia(MOBILE_VOLUME_PRICING_MEDIA_QUERY).matches) return;
    event.stopPropagation();
    // Prevent the default browser behavior for touch gestures on the trigger and popover
    // to avoid triggering scroll in parent carousels when interacting with the pricing tag.
    if (event.cancelable && event.type === 'touchmove') {
      event.preventDefault();
    }
  };

  trigger.addEventListener('pointerdown', stopGesturePropagation, { passive: true });
  trigger.addEventListener('touchstart', stopGesturePropagation, { passive: true });
  trigger.addEventListener('pointermove', stopGesturePropagation, { passive: false });
  trigger.addEventListener('touchmove', stopGesturePropagation, { passive: false });

  const anchoredPopoverElement = anchoredPopover instanceof Component ? anchoredPopover.refs?.popover : null;
  if (anchoredPopoverElement) {
    anchoredPopoverElement.addEventListener('pointerdown', stopGesturePropagation, { passive: true });
    anchoredPopoverElement.addEventListener('touchstart', stopGesturePropagation, { passive: true });
    anchoredPopoverElement.addEventListener('pointermove', stopGesturePropagation, { passive: false });
    anchoredPopoverElement.addEventListener('touchmove', stopGesturePropagation, { passive: false });
  }

    /** @param {HTMLElement} popover */
    const positionMobilePopover = (popover) => {
      const isMobile = window.matchMedia(MOBILE_VOLUME_PRICING_MEDIA_QUERY).matches;
      if (!isMobile) return;

      const triggerRect = trigger.getBoundingClientRect();
      const popoverRect = popover.getBoundingClientRect();
      const margin = 12;
      const spacing = 8;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const popoverWidth = Math.min(popoverRect.width || 320, viewportWidth - margin * 2);
      const popoverHeight = popoverRect.height || 0;
      const spaceAbove = triggerRect.top - margin;
      const spaceBelow = viewportHeight - triggerRect.bottom - margin;
      const openAbove = spaceBelow < popoverHeight + spacing && spaceAbove > spaceBelow;

      const left = Math.max(
        margin,
        Math.min(triggerRect.left + triggerRect.width / 2 - popoverWidth / 2, viewportWidth - popoverWidth - margin)
      );
      const top = openAbove
        ? Math.max(margin, triggerRect.top - popoverHeight - spacing)
        : Math.min(viewportHeight - popoverHeight - margin, triggerRect.bottom + spacing);

      popover.dataset.mobilePosition = 'fixed';
      popover.dataset.mobilePlacement = openAbove ? 'top' : 'bottom';
      popover.style.setProperty('--volume-pricing-mobile-left', `${left}px`);
      popover.style.setProperty('--volume-pricing-mobile-top', `${top}px`);
    };

  trigger.addEventListener(
    'click',
    /** @param {MouseEvent} event */ (event) => {
      const isMobile = window.matchMedia(MOBILE_VOLUME_PRICING_MEDIA_QUERY).matches;

      if (isMobile) {
        event.preventDefault();
        event.stopPropagation();

        const popoverId = trigger.dataset.popoverTarget;
        const popover = popoverId ? document.getElementById(popoverId) : null;
        if (popover) {
          /** @type {HTMLElement & { showPopover?: () => void; hidePopover?: () => void }} */
          const popoverElement = /** @type {any} */ (popover);
          if (popoverElement.matches(':popover-open') || popoverElement.classList.contains('popover-open')) {
            if ('hidePopover' in popoverElement) {
              /** @type {any} */ (popoverElement).hidePopover();
            } else {
              /** @type {any} */ (popoverElement).style.display = 'none';
            }
            popoverElement.removeAttribute('data-mobile-position');
            popoverElement.removeAttribute('data-mobile-placement');
            popoverElement.style.removeProperty('--volume-pricing-mobile-left');
            popoverElement.style.removeProperty('--volume-pricing-mobile-top');
          } else {
            closeAllVolumePricingPopovers();
            if ('showPopover' in popoverElement) {
              /** @type {any} */ (popoverElement).showPopover();
            } else {
              /** @type {any} */ (popoverElement).style.display = 'block';
            }

            requestAnimationFrame(() => {
              positionMobilePopover(popoverElement);
            });
          }
        }
      }
    }
  );
}

  /**
   * Ensures the parent anchored-popover-component refs are refreshed immediately,
   */
  updatedCallback() {
    super.updatedCallback();
    const anchoredPopover = this.closest('anchored-popover-component');
    if (anchoredPopover instanceof Component) {
      anchoredPopover.updatedCallback();
    }
  }

  /**
   * Updates the highlighted price tier based on current quantity
   * @param {number} quantity - The current quantity
   */
  updateActiveTier(quantity) {
    const anchoredPopover = this.closest('anchored-popover-component');
    const popover = anchoredPopover instanceof Component ? anchoredPopover.refs?.popover : null;
    const rows = popover?.querySelectorAll('.volume-pricing-info__row[data-quantity]');
    if (!rows) return;

    let activeTier = null;
    for (const row of rows) {
      row.classList.remove('volume-pricing-info__row--active');
      if (row instanceof HTMLElement && quantity >= parseInt(row.dataset.quantity || '0')) {
        activeTier = row;
      }
    }
    activeTier?.classList.add('volume-pricing-info__row--active');
  }
}

if (!customElements.get('volume-pricing-info')) {
  customElements.define('volume-pricing-info', VolumePricingInfoComponent);
}
